import { arrayMove } from '@dnd-kit/sortable';
import { useEffect, useState } from 'react';
import { useBatchUpdateCatalogSections } from '../../../../../../services/modules/catalogs/catalogs.mutations';
import useIsAdminSeller from '../../../../../../stores/ShowUnavailableProducts/hooks/useIsAdminSeller';
import { useCatalogSections } from '../../contexts/CatalogSections.context';
import { useCatalogView } from '../../contexts/CatalogView.context';
import { ReorderSectionsParams } from '../../hooks/useCatalogSectionsDnd.types';
import { resolveCanManageSections } from '../../utils/resolveCanManageSections';
import { resolveOptimisticSectionOrder } from '../../utils/resolveOptimisticSectionOrder';
import { CatalogSectionTabsBar } from '../CatalogSectionTabsBar/CatalogSectionTabsBar';
import { CatalogSectionTab } from '../CatalogSectionTabsBar/catalogSectionTabsBar.types';
import { CatalogSectionsDropdown } from '../CatalogSectionsDropdown/CatalogSectionsDropdown';
import { CatalogSectionTabsSlotProps } from './catalogSectionTabsSlot.types';

export const CatalogSectionTabsSlot = ({
  catalogId,
  sections,
  isCatalogSettled,
  isPreferencesSettled,
  canPersist,
  readMode,
}: CatalogSectionTabsSlotProps) => {
  const { applied } = useCatalogView();
  const { activeSectionId, selectSection } = useCatalogSections();
  const [managementAnchor, setManagementAnchor] = useState<HTMLElement | null>(null);
  const [optimisticIds, setOptimisticIds] = useState<string[] | null>(null);
  const { mutate: applySections } = useBatchUpdateCatalogSections({ catalogId });
  const isAdminSeller = useIsAdminSeller();

  const orderedSections = resolveOptimisticSectionOrder({ sections, optimisticIds });
  const serverOrderKey = sections.map((section) => section.catalogSectionId).join('|');

  useEffect(() => {
    if (optimisticIds && optimisticIds.join('|') === serverOrderKey) {
      setOptimisticIds(null);
    }
  }, [optimisticIds, serverOrderKey]);

  const canManage = resolveCanManageSections({
    appliedGroupBy: applied.groupBy,
    isCatalogSettled,
    isPreferencesSettled,
    canPersist,
    readMode,
    isAdminSeller,
  });

  const handleReorder = ({ fromIndex, toIndex }: ReorderSectionsParams) => {
    const reordered = arrayMove(orderedSections, fromIndex, toIndex);
    setOptimisticIds(reordered.map((section) => section.catalogSectionId));

    applySections(
      {
        sections: reordered.map((section) => ({
          id: section.catalogSectionId,
          name: section.catalogSectionName,
        })),
      },
      { onError: () => setOptimisticIds(null) },
    );
  };

  const handleSelectFromDropdown = (sectionId: string) => {
    selectSection(sectionId);
    setManagementAnchor(null);
  };

  const tabs: CatalogSectionTab[] = orderedSections.map((section) => ({
    id: section.catalogSectionId,
    name: section.catalogSectionName,
    itemsCount: section.itemsCount,
  }));

  return (
    <>
      <CatalogSectionTabsBar
        sections={tabs}
        activeSectionId={activeSectionId}
        canManage={canManage}
        isManagementOpen={managementAnchor !== null}
        onSelect={selectSection}
        onOpenManagement={setManagementAnchor}
        onReorder={handleReorder}
      />
      {managementAnchor !== null && (
        <CatalogSectionsDropdown
          anchorEl={managementAnchor}
          catalogId={catalogId}
          canManage={canManage}
          sections={orderedSections}
          onClose={() => setManagementAnchor(null)}
          onSelectSection={handleSelectFromDropdown}
        />
      )}
    </>
  );
};
